import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useResource } from '@/hooks/useResource';
import { queryKeys } from '@/lib/queryKeys';
import useDeleteInterval from '@/modules/Planning/hooks/intervals/useDeleteInterval';
import useUpdateInterval from '@/modules/Planning/hooks/intervals/useUpdateInterval';
import type { Interval, Task } from '@/types/types';

import { useIntervalDrawerContext } from './IntervalDrawerContext';

export function useIntervalDrawerAdapter(interval: Interval, taskId?: Task['id']) {
  const queryClient = useQueryClient();
  const { close } = useIntervalDrawerContext();
  const updateInterval = useUpdateInterval();
  const deleteInterval = useDeleteInterval();
  const [formState, setFormState] = useState<Partial<Interval>>({});

  const { data: resource, isLoading } = useResource({
    id: interval.intervalable_id,
    projectId: interval.project_id,
  });

  useEffect(() => {
    setFormState({ ...interval });
  }, [interval]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.currentTarget;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleExcludeNonWorkingDays = (checked: boolean) => {
    setFormState((prev) => ({ ...prev, exclude_non_working_days: checked }));
  };

  const handleSubmit = () => {
    updateInterval.mutate({
      interval: formState,
      projectId: interval.project_id,
      intervalId: interval.id,
    });
    close();
  };

  const handleDelete = () => {
    deleteInterval.mutate(interval.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.intervals.byIntervalableIds(String(resource?.resourceable_id || ''))
            .queryKey,
        });
        close();
      },
    });
  };

  return {
    resource,
    isLoading,
    formState,
    taskId,
    handleChange,
    handleToggleExcludeNonWorkingDays,
    handleSubmit,
    handleDelete,
  };
}
